import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '@shared/components/notifications';
import { useAuthContext } from '@shared/components';
import { trpc } from '@/utils/trpc';
import { subjectApi } from '../api/subjectApi';
import { subjectQueryKeys } from './queryKeys';
import { SubjectUserFormData, RemoveSubjectUserParams, SubjectUserTableData } from '../types';
import { userInvitationApi, InviteUserWithRoleDTO } from '../../user-invitation';
import {
  useUsersWithRoles,
  useUserExists,
  UserWithRoleDTO,
  UserRole,
  UserRoleInfoDTO,
  userManagementQueryKeys,
} from '../../user-management';

// Transform UserWithRoleDTO to SubjectUserTableData
const transformUserWithRoleToSubjectUser = (
  userWithRole: UserWithRoleDTO,
  subjectId: string
): SubjectUserTableData[] => {
  if (!userWithRole.roles || userWithRole.roles.length === 0) {
    return [];
  }

  // Filter roles to only include subject-specific roles for this subject
  const subjectRoles = userWithRole.roles.filter(
    (role: UserRoleInfoDTO) =>
      role.subjectId === subjectId &&
      (role.role === UserRole.PROFESSOR || role.role === UserRole.COURSE_REPRESENTATIVE)
  );

  return subjectRoles.map((role: UserRoleInfoDTO) => ({
    id: userWithRole.id,
    email: userWithRole.email,
    name: userWithRole.name,
    role: role.role,
    assignedAt: role.assignedAt,
    assignedBy: role.assignedBy,
    userRoleId: role.userRoleId,
    userId: userWithRole.id,
    tenantId: userWithRole.tenantId,
    domainId: role.domainId || '',
    subjectId: role.subjectId || subjectId,
    expiresAt: role.expiresAt,
    isExpired: role.isExpired,
    status: userWithRole.status,
  }));
};

// Get subject users by subject
export const useSubjectUsers = (tenantId: string, subjectId: string) => {
  const { data: usersWithRoles, ...queryResult } = useUsersWithRoles({
    tenantId,
    subjectId,
    includeUsersWithoutRoles: false, // Only get users with roles
  });

  const transformedData =
    usersWithRoles?.flatMap((user: UserWithRoleDTO) =>
      transformUserWithRoleToSubjectUser(user, subjectId)
    ) || [];

  return {
    ...queryResult,
    data: transformedData,
  };
};

// Invite subject user mutation - UPDATED to use unified API
export const useInviteSubjectUser = (tenantId: string, domainId: string, subjectId: string) => {
  const queryClient = useQueryClient();
  const { getCurrentUserId } = useAuthContext();

  return useMutation({
    mutationFn: async (data: SubjectUserFormData) => {
      // Convert to unified DTO
      const unifiedData: InviteUserWithRoleDTO = {
        email: data.email,
        name: data.name,
        role: data.role,
        tenantId,
        domainId,
        subjectId,
        assignedBy: getCurrentUserId(),
      };

      const response = await userInvitationApi.inviteUserWithRole(unifiedData);
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate legacy subject queries
      queryClient.invalidateQueries({
        queryKey: subjectQueryKeys.subjectUsersBySubject(tenantId, subjectId),
      });

      // Invalidate user management queries that the table actually uses
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.bySubject(subjectId),
      });
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.byTenant(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.usersWithRoles({ tenantId, subjectId }),
      });

      notificationService.success((data as any)?.message || 'Subject user invited successfully');
    },
    onError: (error: unknown) => {
      console.error('Invite subject user failed:', error);
      notificationService.error(
        (error as any)?.response?.data?.message || 'Failed to invite subject user'
      );
    },
  });
};

// Remove subject user mutation
export const useRemoveSubjectUser = (tenantId: string, domainId: string, subjectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: RemoveSubjectUserParams) => {
      await subjectApi.subjectUserManagement.removeSubjectUserRole(
        params.userId,
        tenantId,
        domainId,
        subjectId,
        params.userRoleId
      );
    },
    onSuccess: (_, params) => {
      // Invalidate legacy subject queries
      queryClient.invalidateQueries({
        queryKey: subjectQueryKeys.subjectUsersBySubject(tenantId, subjectId),
      });

      // Invalidate user management queries that the table actually uses
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.bySubject(subjectId),
      });
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.byTenant(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.usersWithRoles({ tenantId, subjectId }),
      });

      const roleDisplayNames: Record<string, string> = {
        PROFESSOR: 'Lecturer',
        COURSE_REPRESENTATIVE: 'Course Representative',
      };
      const roleDisplayName = roleDisplayNames[params.role] || params.role;

      notificationService.success(`${roleDisplayName} role removed from "${params.userName}"`);
    },
    onError: (error: unknown) => {
      console.error('Remove subject user failed:', error);
      notificationService.error(
        (error as any)?.response?.data?.message || 'Failed to remove subject user role'
      );
    },
  });
};

// Check if user exists
export const useCheckUserExists = (email: string) => {
  return useUserExists(email);
};

// Get subject info by ID - uses tRPC for type-safe API calls
export const useSubjectInfo = (tenantId: string, subjectId: string) => {
  return trpc.subject.getById.useQuery(
    { tenantId, subjectId },
    {
      enabled: !!tenantId && !!subjectId,
      staleTime: 10 * 60 * 1000, // 10 minutes - subject info doesn't change often
    }
  );
};
